import * as Sentry from '@sentry/browser';
import { SeverityLevel } from '@sentry/browser';
import { fetchAddresses } from '~/actions/address.actions';
import { ActionKeys } from '~/actions/constants';
import * as gtmActions from '~/actions/gtm.actions';
import { fetchLabelSettings } from '~/actions/labels-settings.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import { fetchStores } from '~/actions/stores.actions';
import { dismissPromoApplicationNotice, refreshUser } from '~/actions/user.actions';
import { AxiosError, AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import { SET_PRIMARY_CARRIER } from '~/constants/gtm';
import { StoreAsset } from '~/constants/stores';
import {
  BusinessNeed,
  BusinessType,
  OnboardingLocalStorageKeys,
  OrdersMechanism,
} from '~/pages/Onboarding/onboarding.constants';
import * as onboardingActions from '~/pages/Onboarding/store/onboarding.actions';
import {
  completeOnboarding,
  completeStep,
  navigate,
  onboardingTrackEvent,
  prepopulateOnboarding,
  trackStepCompleted,
  updateOnboardingInfoError,
  updateOnboardingInfoSuccess,
  updateOnboardingOrdersError,
  updateOnboardingOrdersSuccess,
  updateOnboardingPreferencesError,
  updateOnboardingPreferencesSuccess,
  updateOnboardingStructure,
} from '~/pages/Onboarding/store/onboarding.actions';
import { LabelsDefaultLabelFormatFormValues } from '~/pages/Settings/Labels/LabelsDefaultForm';
import { startStoreConnect } from '~/pages/StoreConnections/store/store-connect.actions';
import { all, call, put, race, take, takeEvery } from 'redux-saga/effects';
import { selectState } from '~/sagas/index';
import { getDefaultLabelFormat } from '~/selectors/label-settings.selectors';
import { getParcelTemplates } from '~/selectors/parcel-template.selectors';
import { getConnectedStores } from '~/selectors/stores.selectors';
import { getUserAddress, getUserId } from '~/selectors/user.selectors';
import * as addressService from '~/services/address.service';
import { editApiAddress } from '~/services/address.service';
import * as OnboardingService from '~/services/onboarding.service';
import settingsService from '~/services/settings.service';
import Address, { sanitizeAddress } from '~/types/Address';
import * as APIAddress from '~/types/APIAddress';
import { newAPIAddress } from '~/types/APIAddress';
import LabelFormat from '~/types/LabelFormat';
import { labelSettingsToJSON } from '~/types/LabelSettings';
import ParcelTemplate from '~/types/ParcelTemplate';
import Store, { StoreName, StoreStatusName } from '~/types/Store';
import { getCookie } from '~/utils/cookies';
import { Events, identify, track } from '~/utils/segment';
import { OnboardingUserDetail } from '../onboarding.types';
import * as plansService from '~/services/plans.service';
import * as plansActions from '~/actions/plans.actions';
import {
  OnboardingApi,
  OnboardingInfoState,
  OnboardingOrdersState,
  OnboardingPreferencesState,
  OnboardingProPlanState,
  OnboardingState,
} from './onboarding.reducers';
import {
  getConnectedStoreName,
  getNavigationCurrentStep,
  getNavigationCurrentStepName,
  getNavigationLastCompletedStep,
  getNavigationLastCompletedStepName,
  getOnboardingApi,
  getOnboardingBusinessNeed,
  getOnboardingBusinessType,
  getOnboardingInfo,
  getOnboardingOrders,
  getOnboardingPlan,
  getOnboardingPreferences,
  getOnboardingStore,
  getOnboardingUserInfo,
  getOrderMechanism,
  getOrdersAddressObject,
  getPreferencesAddressObject,
} from './onboarding.selectors';
import { UserPlan, userPlanFromJSON } from '~/types/Plan';

import { planToTrackingDetails } from '~/utils/plans.util';
import { setupAuthentication } from '~/services/api';
import { getCurrentPlan } from '~/selectors/plans.selectors';
import { getOnboarding2024FlagStorage } from '~/pages/Onboarding2024/utils/onboarding';

export function* safeNavigate(path: string) {
  if (path.indexOf('/') === 0) {
    yield put(push({ pathname: path }));
  } else {
    window.location.href = path;
  }
}

export function* persistStatus() {
  try {
    yield call(
      [localStorage, localStorage.setItem],
      OnboardingLocalStorageKeys.ONBOARDING_ACTIVE,
      'true',
    );
    yield put(onboardingTrackEvent('Onboarding - Started Onboarding'));
    // Dismiss the promo application notice, if applicable because its shown in the welcome modal
    yield put(dismissPromoApplicationNotice());
    if (!window.location.pathname.match('^/onboarding')) yield safeNavigate('/onboarding/welcome');
  } catch (error) {
    Sentry.captureMessage('Error setting onboarding status to local storage', {
      level: 'fatal' as SeverityLevel,
    });
  }
}

export function* persistConnectedStore(stores: Store[]) {
  const connectedStores = stores.filter(
    (store) => store?.status?.name === StoreStatusName.Connected,
  );
  if (connectedStores.length) {
    yield call(
      [localStorage, localStorage.setItem],
      OnboardingLocalStorageKeys.ONBOARDING_ACTIVE_STORE,
      connectedStores[0].storePlatform.name,
    );
  }
}

export function* hydrateOnboarding() {
  // Disable old onboarding
  yield put(fetchStores());
  yield put(fetchLabelSettings());
  yield put(fetchAddresses());
  yield all([
    race({
      address: take(ActionKeys.ADDRESSES_FETCH_SUCCESS),
      addressError: take(ActionKeys.ADDRESSES_FETCH_ERROR),
    }),
    take(ActionKeys.FETCH_LABEL_SETTINGS_SUCCESS),
    race({
      address: take(ActionKeys.STORES_FETCH_SUCCESS),
      addressError: take(ActionKeys.STORES_FETCH_ERROR),
    }),
  ]);
  const userInfo: OnboardingUserDetail =
    yield selectState<OnboardingUserDetail>(getOnboardingUserInfo);
  const address: Maybe<APIAddress.APIAddress> =
    yield selectState<Maybe<APIAddress.APIAddress>>(getUserAddress);
  const connectedStores: Store[] = yield selectState<Store[]>(getConnectedStores);
  const defaultLabelFormat: LabelFormat = yield selectState<LabelFormat>(getDefaultLabelFormat);
  yield persistConnectedStore(connectedStores);
  yield put(
    prepopulateOnboarding({
      ...userInfo,
      address: address as Address,
      defaultLabelFormat,
      connectedStores,
    }),
  );
}

export function* persistLastCompletedStep(
  action: ReturnType<typeof onboardingActions.completeStep>,
) {
  try {
    yield call(
      [localStorage, localStorage.setItem],
      OnboardingLocalStorageKeys.ONBOARDING_LAST_COMPLETED_STEP,
      action.payload.index.toString(),
    );
  } catch (error) {
    Sentry.captureMessage('Error setting last completed onboarding step to local storage', {
      level: 'fatal' as SeverityLevel,
    });
  }
}

export function* persistCurrentStep(action: ReturnType<typeof onboardingActions.navigate>) {
  try {
    yield call(
      [localStorage, localStorage.setItem],
      OnboardingLocalStorageKeys.ONBOARDING_CURRENT_STEP,
      action.payload.index.toString(),
    );
  } catch (error) {
    Sentry.captureMessage('Error setting current onboarding step to local storage', {
      level: 'fatal' as SeverityLevel,
    });
  }
}

export function* persistBusinessTypeNeed(
  action: ReturnType<typeof onboardingActions.updateOnboardingStructure>,
) {
  try {
    yield call(
      [localStorage, localStorage.setItem],
      OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_TYPE,
      action.payload.businessType,
    );
    yield call(
      [localStorage, localStorage.setItem],
      OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_NEED,
      action.payload.businessNeed,
    );
  } catch (error) {
    Sentry.captureMessage(
      'Error setting onboarding business type and business need to localstorage',
      {
        level: 'fatal' as SeverityLevel,
      },
    );
  }
}

export function* sendOnboardingTrackEvent(
  action: ReturnType<typeof onboardingActions.onboardingTrackEvent>,
) {
  try {
    const currentStep: number = yield selectState<number>(getNavigationCurrentStep);
    const currentStepName: string = yield selectState<string>(getNavigationCurrentStepName);
    const lastCompletedStep: number = yield selectState<number>(getNavigationLastCompletedStep);
    const lastCompletedStepName: string = yield selectState<string>(
      getNavigationLastCompletedStepName,
    );
    const businessType: string = yield selectState<string>(getOnboardingBusinessType);

    const userOnboardingData = {
      currentStepNumber: currentStep,
      currentStepName: currentStepName,
      lastCompletedStepNumber: lastCompletedStep,
      lastCompletedStepName: lastCompletedStepName,
      onboardingBusinessType: businessType,
    };
    const allUserProperties = {
      ...userOnboardingData,
      ...action.payload.properties,
    };
    yield put(sendTrackEvent(action.payload.name, allUserProperties));
  } catch (error) {
    Sentry.captureException('Error sending onboarding track event', {
      level: 'fatal' as SeverityLevel,
      extra: {
        error,
        onboardingStore: yield selectState<OnboardingState>(getOnboardingStore),
      },
    });
  }
}

function* addOrUpdateAddress(addressObject: APIAddress.APIAddress, address: Address) {
  const apiAddress = newAPIAddress(<APIAddress.default>{
    ...address,
  });
  const addressReq = APIAddress.toJSON({
    ...sanitizeAddress(apiAddress),
    isDefaultSender: true,
    isDefaultReturn: true,
  }) as APIAddress.JSONAPIAddress;

  const response: AxiosResponse<APIAddress.JSONAPIAddress> =
    addressObject && addressObject.id
      ? yield call(editApiAddress, addressObject.id, { id: addressObject.id, ...addressReq })
      : yield call(addressService.createApiAddress, addressReq);

  yield put(refreshUser());
  return APIAddress.fromJSON(response.data)!;
}

export function* handleError(view: string, error: AxiosError) {
  yield put(
    onboardingTrackEvent('Onboarding - API Error', {
      view,
      error,
    }),
  );
  if (error && error.response) {
    Sentry.captureMessage('Onboarding Address API Error', {
      level: 'warning' as SeverityLevel,
      extra: {
        responseBody: JSON.stringify(error.response.data),
      },
    });
  }
}

export function* updateOnboardingInfo(
  action: ReturnType<typeof onboardingActions.updateOnboardingInfo>,
) {
  const shippoUseCaseMap = {
    business: 'Business',
    personal: 'Personal',
    partner: 'Partner',
  } as const;

  const useCase = shippoUseCaseMap.hasOwnProperty(action.payload.values.businessType)
    ? shippoUseCaseMap[action.payload.values.businessType as keyof typeof shippoUseCaseMap]
    : undefined;

  try {
    const sendAdditionalInformationPayload = {
      company_name: action.payload.values.companyName || undefined,
      monthly_packages: action.payload.values.monthlyVolume || undefined,
      phone: action.payload.values.phone || undefined,
      what_type_of_business: action.payload.values.businessNeed || undefined,
      shippo_use_case: useCase,
    };
    // If the user is updating their info right after signup, they are not authenticated just yet
    if (action.payload.token) {
      yield call(setupAuthentication, action.payload.token);
    }
    yield call(OnboardingService.sendAdditionalInformation, sendAdditionalInformationPayload);
    yield put(updateOnboardingInfoSuccess(action.payload.values));

    if (action.payload.values.country?.name) {
      yield call(
        [localStorage, localStorage.setItem],
        OnboardingLocalStorageKeys.ONBOARDING_USER_COUNTRY,
        JSON.stringify(action.payload.values.country),
      );
    }
  } catch (error) {
    yield call(handleError, 'info', error as AxiosError);
    yield put(updateOnboardingInfoError(error as AxiosError));
  }
}

/**
 * Helper to navigate forward
 */
export function* navigateForward() {
  const currentStep: number = yield selectState<number>(getNavigationCurrentStep);
  const lastCompletedStep: number = yield selectState<number>(getNavigationLastCompletedStep);
  if (lastCompletedStep <= currentStep) {
    yield put(completeStep(currentStep));
  }
  yield put(navigate(currentStep + 1));
}

export function* handleOnboardingInfoSuccess() {
  const storeName: string = yield selectState<Maybe<string>>(getConnectedStoreName);
  const businessType: string = yield selectState<string>(getOnboardingBusinessType);
  const businessNeed: string = yield selectState<string | undefined>(getOnboardingBusinessNeed);
  if (getOnboarding2024FlagStorage() !== 'true') {
    yield put(trackStepCompleted('info', 'advance'));
  }
  yield put(
    updateOnboardingStructure(
      businessType as BusinessType,
      businessNeed as keyof typeof BusinessNeed,
      storeName,
    ),
  );
  yield navigateForward();
}

export function* updateOnboardingPreferences(
  action: ReturnType<typeof onboardingActions.updateOnboardingPreferences>,
) {
  try {
    const hubspotPayload = {
      primary_carrier: action.payload.primaryCarrier,
      apply_insurance_on_all: action.payload.applyInsuranceOnAll,
    };
    const onboardingDefaultLabelPayload: LabelsDefaultLabelFormatFormValues = {
      defaultLabelFormat: {
        value: action.payload.labelSize as string,
        label: action.payload.labelSize as string,
      },
      refText1: '',
      refText2: '',
      isMergeLabelsAndPackingSlips: false,
      isUseOrderNumber: false,
      isRequireSignatureConfirmation: false,
      isAutoCreateReturnLabels: false,
    };
    const labelReqbody = labelSettingsToJSON(onboardingDefaultLabelPayload);
    yield call(settingsService.saveLabelSettings, labelReqbody);
    yield call(OnboardingService.sendAdditionalInformation, hubspotPayload);

    if (action.payload.primaryCarrier) {
      yield call(
        [localStorage, localStorage.setItem],
        'primary_carrier',
        action.payload.primaryCarrier as string,
      );
      yield put(
        gtmActions.sendGTMEvent({
          eventName: SET_PRIMARY_CARRIER,
          data: {
            primaryCarrier: action.payload.primaryCarrier,
          },
        }),
      );
    }

    if (action.payload.address) {
      const addressObject: Address | null = yield selectState<Address | null>(
        getPreferencesAddressObject,
      );
      const addressObjectRes: APIAddress.SavedAPIAddress = yield addOrUpdateAddress(
        addressObject!,
        {
          ...action.payload.address,
        },
      );
      yield put(
        updateOnboardingPreferencesSuccess(
          action.payload.primaryCarrier,
          action.payload.labelSize,
          addressObjectRes as Address,
        ),
      );
      yield put(
        trackStepCompleted('preferences', action.payload.complete ? 'get_started' : 'advance'),
      );
      yield put(onboardingTrackEvent('Onboarding - Filled Sender Address'));
    } else {
      yield put(
        updateOnboardingPreferencesSuccess(action.payload.primaryCarrier, action.payload.labelSize),
      );
      yield put(
        trackStepCompleted('preferences', action.payload.complete ? 'get_started' : 'advance'),
      );
    }
    if (action.payload.complete) {
      yield call(updateUserPlan);
      yield persistOnboardingCompletion();
      yield safeNavigate('/orders');
    }
    const userId: Maybe<number> = yield selectState<Maybe<number>>(getUserId);
    yield call(identify, userId!, { preferred_carrier: action.payload.primaryCarrier });
  } catch (error) {
    yield call(handleError, 'preferences', error as AxiosError);
    yield put(updateOnboardingPreferencesError(error as AxiosError));
    Sentry.captureMessage('Error submitting Preference view information to Hubspot and DB', {
      level: 'fatal' as SeverityLevel,
      extra: {
        error,
      },
    });
  }
}

export function* updateOnboardingOrders(
  action: ReturnType<typeof onboardingActions.updateOnboardingOrders>,
) {
  try {
    const addressObject: Address | null = yield selectState<Address | null>(getOrdersAddressObject);
    const currentStepName: string = yield selectState<string>(getNavigationCurrentStepName);
    const isOrdersView = currentStepName === 'newOnboarding.nav.orders';
    if (action.payload.address) {
      const addressObjectRes: APIAddress.SavedAPIAddress = yield addOrUpdateAddress(
        addressObject!,
        {
          ...action.payload.address,
        },
      );
      yield put(
        updateOnboardingOrdersSuccess({
          ...action.payload,
          addressObject: addressObjectRes,
        }),
      );
      yield put(onboardingTrackEvent('Onboarding - Filled Sender Address'));
    } else {
      yield put(
        updateOnboardingOrdersSuccess({
          ...action.payload,
          addressObject: addressObject as Address,
        }),
      );
    }
    // Send completed flow event with condition button text based on whether order is selected as order mechanism
    if (action.payload.orderMechanism === 'store') {
      yield put(trackStepCompleted(isOrdersView ? 'orders' : 'sender_address', 'connect_store'));
      yield put(
        onboardingTrackEvent('Onboarding - Completed Flow', {
          'Completed Flow Button': 'Connect Store',
        }),
      );
    } else {
      yield put(trackStepCompleted(isOrdersView ? 'orders' : 'sender_address', 'get_started'));
      yield put(
        onboardingTrackEvent('Onboarding - Completed Flow', {
          'Completed Flow Button': 'Get Started',
        }),
      );
    }

    yield put(
      completeOnboarding(getCookie('shippo_signup_cookie') === 'api' ? '/settings/api' : ''),
    );
  } catch (error) {
    yield call(handleError, 'orders', error as AxiosError);
    yield put(updateOnboardingOrdersError(error as AxiosError));
  }
}

function* persistOnboardingCompletion() {
  const orderMechanism: OrdersMechanism = yield selectState<OrdersMechanism>(getOrderMechanism);

  // Add required localStorage entries for onboarding welcome message
  const businessType: string = yield selectState<string>(getOnboardingBusinessType);
  const businessNeed: string | undefined = yield selectState<string | undefined>(
    getOnboardingBusinessNeed,
  );
  if (
    (businessNeed !== 'Developer' && businessType !== 'partner') ||
    (businessType === 'partner' && businessNeed === 'Other')
  ) {
    yield call(
      [localStorage, localStorage.setItem],
      OnboardingLocalStorageKeys.ONBOARDING_SHOW_WELCOME_MESSAGE,
      'true',
    );
    yield call(
      [localStorage, localStorage.setItem],
      OnboardingLocalStorageKeys.ONBOARDING_ORDER_MECHANISM,
      orderMechanism,
    );
  }

  // Remove the onboarding-related localStorage entries
  yield call([localStorage, localStorage.removeItem], OnboardingLocalStorageKeys.ONBOARDING_ACTIVE);
  // CURRENTLY NEED THIS FOR GET STARTED EXPERIMENT
  // yield call(
  //   [localStorage, localStorage.removeItem],
  //   OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_TYPE,
  // );
  yield call(
    [localStorage, localStorage.removeItem],
    OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_NEED,
  );
  yield call(
    [localStorage, localStorage.removeItem],
    OnboardingLocalStorageKeys.ONBOARDING_LAST_COMPLETED_STEP,
  );
  yield call(
    [localStorage, localStorage.removeItem],
    OnboardingLocalStorageKeys.ONBOARDING_CURRENT_STEP,
  );
  yield call(
    [localStorage, localStorage.removeItem],
    OnboardingLocalStorageKeys.ONBOARDING_ACTIVE_STORE,
  );
  yield call(
    [localStorage, localStorage.removeItem],
    OnboardingLocalStorageKeys.ONBOARDING_USER_COUNTRY,
  );
  yield call(
    [localStorage, localStorage.removeItem],
    OnboardingLocalStorageKeys.ONBOARDING_COMPLETED,
  );
}

const getStoreConnectParams = ({ storeSelection, storeConnectProps }: OnboardingOrdersState) => {
  switch ((storeSelection as StoreAsset).name) {
    case StoreName.Amazon:
      return { country: storeConnectProps };
    case StoreName.Shopify:
      return { shop: storeConnectProps };
    default:
      return {};
  }
};

export function* onCompleteOnboarding(
  action: ReturnType<typeof onboardingActions.completeOnboarding>,
) {
  yield call(updateUserPlan);

  const connectedStore: string | undefined | null = yield selectState<string | undefined | null>(
    getConnectedStoreName,
  );
  // Sends onboarding completed event to Segment
  yield call(track, Events.onboarding_completed, {
    business_type: localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_TYPE) ?? '',
    business_need: localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_NEED) ?? '',
  });

  yield persistOnboardingCompletion();
  if (action.payload.url) {
    yield safeNavigate(action.payload.url);
  } else {
    const ordersState: OnboardingOrdersState =
      yield selectState<OnboardingOrdersState>(getOnboardingOrders);
    if (
      ordersState.orderMechanism === 'store' &&
      (ordersState.storeSelection?.connectLink || ordersState.storeSelection?.connectRoute) &&
      !connectedStore
    ) {
      yield put(
        onboardingTrackEvent('Onboarding - Initial Connected Store', {
          store: ordersState.storeSelection.name,
        }),
      );
      // readyForUrl arg is set to 'false' to work with the new store-connect flow
      yield put(
        startStoreConnect(
          ordersState.storeSelection,
          false,
          'onboarding',
          getStoreConnectParams(ordersState),
        ),
      );
    } else {
      yield safeNavigate('/orders');
    }
  }
}

export function* onTrackStepCompleted(
  action: ReturnType<typeof onboardingActions.trackStepCompleted>,
) {
  const businessType: string = yield selectState<string>(getOnboardingBusinessType);
  const businessNeed: string | null | undefined = yield selectState<string | null | undefined>(
    getOnboardingBusinessNeed,
  );
  const currentStepIndex: number = yield selectState<number>(getNavigationCurrentStep);
  const currentStepName: string = yield selectState<string>(getNavigationCurrentStepName);
  const lastCompletedStepIndex: number = yield selectState<number>(getNavigationLastCompletedStep);
  const lastCompletedStepName: string = yield selectState<string>(
    getNavigationLastCompletedStepName,
  );
  const addedStepInformation: Record<string, unknown> = {};
  const storeReferral = localStorage.getItem('registration_store') ?? undefined;
  const storeConnectionStarted = action.payload.action === 'connect_store';

  switch (currentStepName) {
    case 'newOnboarding.nav.info':
      const onboardingInfo: OnboardingInfoState =
        yield selectState<OnboardingInfoState>(getOnboardingInfo);

      addedStepInformation['phone_number'] = onboardingInfo.phone;
      addedStepInformation['company_name'] = onboardingInfo.companyName;
      addedStepInformation['monthly_shipment_volume'] = onboardingInfo.monthlyVolume;

      break;
    case 'newOnboarding.nav.preferences':
      const onboardingPreferences: OnboardingPreferencesState =
        yield selectState<OnboardingPreferencesState>(getOnboardingPreferences);
      const packageTemplates: readonly ParcelTemplate[] =
        yield selectState<readonly ParcelTemplate[]>(getParcelTemplates);

      addedStepInformation['label_size'] = onboardingPreferences.labelSize;
      addedStepInformation['added_package_templates'] = packageTemplates.length > 0;
      addedStepInformation['preferred_carrier'] = onboardingPreferences.primaryCarrier;
      addedStepInformation['sender_address'] = APIAddress.toJSON(
        onboardingPreferences.addressObject!,
      );

      break;
    case 'newOnboarding.nav.orders':
      const onboardingOrders: OnboardingOrdersState =
        yield selectState<OnboardingOrdersState>(getOnboardingOrders);

      addedStepInformation['order_mechanism'] = onboardingOrders.orderMechanism;
      addedStepInformation['sender_address'] = APIAddress.toJSON(onboardingOrders.addressObject!);
      addedStepInformation['store_platform'] = onboardingOrders.storeSelection?.name;
      if (storeConnectionStarted)
        addedStepInformation['store_connection_started'] = onboardingOrders.storeSelection?.name;

      break;
    case 'newOnboarding.nav.api':
      const onboardingApi: OnboardingApi = yield selectState<OnboardingApi>(getOnboardingApi);

      addedStepInformation['help_form_submitted'] = onboardingApi.hasSubmittedHubspotForm;

      break;
  }

  yield call(track, Events.onboarding_step_completed, {
    view: action.payload.view,
    action: action.payload.action,
    business_type: businessType,
    business_need: businessNeed,
    current_step_index: currentStepIndex,
    current_step_name: currentStepName,
    last_completed_step_index: lastCompletedStepIndex,
    last_completed_step_name: lastCompletedStepName,
    store_referral: storeReferral,
    ...addedStepInformation,
    ...action.payload.override,
  });
}

export function* updateUserPlanOnboarding(
  action: ReturnType<typeof onboardingActions.updateOnboardingPlan>,
) {
  try {
    // Store values on `localStorage` so we can recover them in case of page refresh
    yield call(
      [localStorage, localStorage.setItem],
      OnboardingLocalStorageKeys.ONBOARDING_PLAN,
      action.payload.plan,
    );

    if (action.payload.completeOnboarding) {
      // Complete onboarding will handle plan upgrading
      yield put(completeOnboarding('/orders'));
    } else {
      yield navigateForward();
    }
  } catch (e) {
    yield call(handleError, 'pro_plan', e as AxiosError);
    yield put(onboardingActions.updateOnboardingProPlanError(e as AxiosError));
    yield put(plansActions.updateUserPlanError(e.toString()));
  }
}

function* updateUserPlan() {
  const { plan } = yield selectState<OnboardingProPlanState>(getOnboardingPlan);

  const currentPlan: Maybe<UserPlan> = yield selectState<Maybe<UserPlan>>((state) =>
    getCurrentPlan(state),
  );

  if (!plan) return;

  // If you update a plan with the same plan, it will return an error
  if (currentPlan?.plan?.name === plan) return;

  const response: AxiosResponse = yield call(plansService.setUserPlan, plan);
  const userPlan: UserPlan = userPlanFromJSON(response.data);
  yield call(track, Events.plan_updated, {
    operation: 'started',
    ...planToTrackingDetails(userPlan),
    previous_plan_name: currentPlan?.plan?.displayName,
    entry_point: 'onboarding',
  });
  yield put(plansActions.updateUserPlanSuccess(userPlan));
}

export default [
  takeEvery(onboardingActions.OnboardingActionKeys.COMPLETE_STEP, persistLastCompletedStep),
  takeEvery(onboardingActions.OnboardingActionKeys.INITIALIZE_ONBOARDING, hydrateOnboarding),
  takeEvery(onboardingActions.OnboardingActionKeys.NAVIGATE, persistCurrentStep),
  takeEvery(onboardingActions.OnboardingActionKeys.START_ONBOARDING, persistStatus),
  takeEvery(
    onboardingActions.OnboardingActionKeys.SEND_ONBOARDING_TRACK_EVENT,
    sendOnboardingTrackEvent,
  ),
  takeEvery(
    onboardingActions.OnboardingActionKeys.UPDATE_ONBOARDING_STRUCTURE,
    persistBusinessTypeNeed,
  ),
  takeEvery(onboardingActions.OnboardingActionKeys.UPDATE_ONBOARDING_INFO, updateOnboardingInfo),
  takeEvery(
    onboardingActions.OnboardingActionKeys.UPDATE_ONBOARDING_INFO_SUCCESS,
    handleOnboardingInfoSuccess,
  ),
  takeEvery(
    onboardingActions.OnboardingActionKeys.UPDATE_ONBOARDING_PREFERENCES,
    updateOnboardingPreferences,
  ),
  takeEvery(
    [onboardingActions.OnboardingActionKeys.UPDATE_ONBOARDING_PREFERENCES_SUCCESS],
    navigateForward,
  ),
  takeEvery(
    onboardingActions.OnboardingActionKeys.UPDATE_ONBOARDING_ORDERS,
    updateOnboardingOrders,
  ),
  takeEvery(onboardingActions.OnboardingActionKeys.COMPLETE_ONBOARDING, onCompleteOnboarding),
  takeEvery(onboardingActions.OnboardingActionKeys.TRACK_STEP_COMPLETED, onTrackStepCompleted),
  takeEvery(
    onboardingActions.OnboardingActionKeys.UPDATE_ONBOARDING_PRO_PLAN,
    updateUserPlanOnboarding,
  ),
];
